/*
 * Social-proof counters for the product page — blocks `people_viewing` and
 * `recent_purchases` in sections/main-product.liquid.
 *
 * The numbers are merchant configuration read from product metafields, NOT live
 * analytics: nothing is fetched, tracked or stored. The value shown on first paint
 * is computed in Liquid (snippets/product-social-proof.liquid) so the line is
 * correct without JavaScript; this file only moves it forward while the page is
 * open, with a single pending timer per element that is cleared on disconnect.
 */
if (!customElements.get('product-social-proof')) {
  const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)');
  const DAY = 86400000;

  customElements.define(
    'product-social-proof',
    class ProductSocialProof extends HTMLElement {
      connectedCallback() {
        // A pending timer means this element is already running — never start a second one.
        if (this.timer) return;

        this.output = this.querySelector('.product-social-proof__count');
        if (!this.output) return;

        this.current = parseInt(this.output.textContent, 10);
        if (this.dataset.mode === 'viewing') this.startViewing();
        else this.startPurchases();
      }

      disconnectedCallback() {
        clearTimeout(this.timer);
        this.timer = null;
      }

      startViewing() {
        const min = parseInt(this.dataset.min, 10);
        const max = parseInt(this.dataset.max, 10);
        // A single possible value can never change — do not run a timer at all.
        if (max <= min) return;

        const delay = Math.max(10, parseInt(this.dataset.interval, 10) || 20) * 1000;
        const tick = () => {
          // Drift by 1-2 in either direction; a step that would leave the range is
          // mirrored back into it, so the value can never fall outside min..max.
          const step = (Math.random() < 0.5 ? -1 : 1) * (Math.random() < 0.5 ? 1 : 2);
          let next = this.current + step;
          if (next < min || next > max) next = this.current - step;
          next = Math.min(max, Math.max(min, next));

          if (next !== this.current) {
            this.current = next;
            this.render(next);
          }
          this.timer = setTimeout(tick, delay);
        };

        this.timer = setTimeout(tick, delay);
      }

      startPurchases() {
        this.base = parseInt(this.dataset.base, 10);
        this.max = parseInt(this.dataset.max, 10);
        this.step = parseInt(this.dataset.step, 10) * 1000;
        // Both offsets come from Liquid, so the 12:00 PM boundary follows the shop's
        // timezone rather than the visitor's clock.
        this.resetAt = Date.now() + parseInt(this.dataset.reset, 10) * 1000;
        this.schedule(parseInt(this.dataset.next, 10) * 1000);
      }

      /*
       * One pending timer at a time: either the next increment or the daily reset,
       * whichever lands first. Once `max` is reached only the reset stays scheduled.
       */
      schedule(delay) {
        while (this.resetAt <= Date.now()) this.resetAt += DAY;
        const untilReset = this.resetAt - Date.now();

        if (this.current >= this.max || untilReset <= delay) {
          this.timer = setTimeout(() => {
            if (this.current !== this.base) {
              this.current = this.base;
              this.render(this.base);
            }
            this.schedule(this.step);
          }, untilReset);
          return;
        }

        this.timer = setTimeout(() => {
          this.current = Math.min(this.max, this.current + (Math.random() < 0.35 ? 2 : 1));
          this.render(this.current);
          this.schedule(this.step);
        }, delay);
      }

      // Only the text node changes; the count's width is reserved in CSS, so the
      // surrounding layout never moves.
      render(value) {
        this.output.textContent = value;
        if (!reduceMotion.matches && this.output.animate) {
          this.output.animate([{ opacity: 0.3 }, { opacity: 1 }], 200);
        }
      }
    }
  );
}
